import React, {
  Fragment,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useContext,
  useRef
} from "react";
import * as R from "ramda";
import {
  Modal,
  Tabs,
  Row,
  Icon,
  Avatar,
  Tag,
  Progress,
  Skeleton,
  Tooltip,
  Checkbox,
  Popover
} from "antd";
import { withTranslation } from "react-i18next";
import { compose, withProps } from "recompose";
import { withRouter } from "react-router-dom";
import { connect, useSelector } from "react-redux";
import moment from "moment";

import MatchPercentage from "../CandidateCard/MatchPercentage";
import Details from "../../components/CandidateDetails";
import { getOptimizedAvatar } from "../../utils/images";
import { isValidJSON } from "../../utils/json";
import { formatAddress } from "../../utils/candidates.js";
import useKeyPress from "../../hooks/custom/useKeyPress";
import Button from "../Button";
import { initials } from "../../utils/strings";
import { isRecent } from "../../utils/dates";
import Date from "../Date";
import { CANDIDATE_LISTS } from "../../constants/opportunity";
import { getCountry } from "../../utils/data/static/countries";
import api from "../../utils/api";

import SentimentalAnalysis from "./SentimentalAnalysis";
import Tests from "./Tests";
import Answers from "./Answers";
import CandidateHistory from "./CandidateHistory";
import MetadataNotes from "./MetadataNotes/MetadataNotes";
import { SuccessProbabilityTag } from "./Tests/SuccessProbabilityTag";

import styled, {
  createGlobalStyle,
  ThemeContext
} from "styled-components/macro";

const modalWidth = 1168;
const arrowSize = 64;
const candidateModalZIndex = 1000;

const MobileModalGlobalStyle = createGlobalStyle`
  @media (max-width: 1024px) {
    html,
    body,
    .ant-modal-wrap {
      overflow-x: hidden !important;
      overflow-y: auto !important;
      -webkit-overflow-scrolling: touch;
    }

    [data-test="candidate-modal"] .ant-tabs-nav .ant-tabs-tab {
      align-items: center !important;
      display: inline-flex !important;
      flex-direction: column !important;
      height: 72px !important;
      justify-content: center !important;
      line-height: 18px !important;
      vertical-align: top !important;
    }

    [data-test="candidate-modal"] .ant-tabs-nav .ant-tabs-tab-active {
      top: 0 !important;
    }

    [data-test="candidate-modal"] .ant-tabs-nav .ant-tabs-tab > .ant-badge {
      display: block !important;
      height: 20px !important;
      line-height: 20px !important;
    }

    [data-test="candidate-modal"] .ant-tabs-nav .ant-tabs-tab > .ant-badge + span {
      display: block !important;
      line-height: 18px !important;
      margin-top: 4px !important;
    }
  }

  @media (min-width: 768px) and (max-width: 1024px) {
    [data-test="candidate-modal"].ant-modal-wrap {
      background: rgba(0, 13, 68, 0.08);
    }

    [data-test="candidate-modal"] .ant-tabs-nav .ant-tabs-tab {
      flex-direction: row !important;
      gap: 6px;
      height: 64px !important;
      min-width: auto !important;
      padding: 18px 24px !important;
    }

    [data-test="candidate-modal"] .ant-tabs-nav .ant-tabs-tab > .ant-badge + span {
      margin-top: 0 !important;
    }
  }

  @media (min-width: 561px) and (max-width: 1024px) {
    [data-test="candidate-modal"] .ant-tabs-tab-prev,
    [data-test="candidate-modal"] .ant-tabs-tab-prev-icon,
    [data-test="candidate-modal"] .ant-tabs-tab-prev-icon-target,
    [data-test="candidate-modal"] .ant-tabs-tab-next,
    [data-test="candidate-modal"] .ant-tabs-tab-next-icon,
    [data-test="candidate-modal"] .ant-tabs-tab-next-icon-target {
      display: none !important;
    }
  }

  @media (min-width: 1025px) and (max-width: 1196px) {
    html,
    body,
    .ant-modal-wrap {
      overflow-x: hidden !important;
    }

    [data-test="candidate-modal"] .ant-modal {
      max-width: calc(100vw - 48px) !important;
      width: calc(100vw - 48px) !important;
    }
  }
`;

const StyledModal = styled(Modal)`
  .ant-modal-body {
    background-image: linear-gradient(to bottom, #f7f9fa, #ffffff);
    padding: 24px 0 0 0;
  }
  .ant-tabs{
    border-width: 0px 1px 1px;
    border-style: none solid solid;
    border-color: currentcolor rgb(232, 232, 232) rgb(232, 232, 232);
    border-right: none !important;
  }

  .ant-tabs-tab {
    border-right: 1px solid #e8e8e8;
    border-top: 1px solid #e8e8e8;
    font-weight: 500;
    line-height: 22px;
    text-align: center;
  }

  .ant-tabs-bar{
    margin: 0;
  }
  .ant-tabs-nav .ant-tabs-tab{
    margin: 0px;
  }

  .ant-tabs-nav-wrap{
    background-color: rgb(247, 249, 250);
  }

  .ant-tabs-tab-active {
    background-color: #ffffff;
  }

  .ant-tabs-ink-bar {
    top: 1px;
  }
  .left {
    background: transparent !important;
  }
  
  .modal-arrow {
    display: flex;
    -webkit-box-pack: center;
    justify-content: center;
    -webkit-box-align: center;
    align-items: center;
    position: fixed;
    top: calc(50% - 64px);
    color: white;
    border: 0px;
    outline: 0px;
    margin: 0px;
    padding: 0px;
    width: 64px;
    height: 64px;
    border-radius: 32px;
    background-color: rgba(52, 58, 64, 0.314);
    font-size: 34px;
    cursor: pointer;
    transition: background-color 0.15s;
  }
  .modal-arrow.arrow-prev, .modal-arrow.arrow-next {
    right: calc(50% + 600px);
  }
  .modal-arrow.arrow-next {
    left: calc(50% + 600px);
  }

  @media (max-width: 1024px) {
    &.ant-modal {
      height: auto;
    
      margin: 0;
      max-width: 100vw;
      padding: 0;
      top: 0;
      width: 99vw !important;
    }

    .ant-modal-content {
      border-radius: 0;
      display: flex;
      flex-direction: column;
    
      overflow: visible;
    }

    .ant-modal-close {
      color: #004cff;
      left: 16px;
      right: auto;
      top: 86px;
      z-index: 2;
    }

    .ant-modal-close-x {
      font-size: 24px;
      height: 40px;
      line-height: 40px;
      width: 40px;
    }

    .ant-modal-body {
      border-radius: 0;
      display: flex;
      flex: 1;
      height: auto;
      max-height: 100dvh;
      min-height: 0;
      overflow-x: hidden;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;

      .kpmtxf {
        flex-direction: column;

        .ant-col-9 {
          width: 100%;
        }

        .ant-col-12 {
          width: 100%;
        }
    }

    .ant-modal-body > div {
      height: auto;
      min-height: 0;
      min-width: 0;
      overflow: visible;
      max-width: 100%;
      width: 100%;
    }

    .ant-modal-wrap,
    .ant-modal,
    .ant-modal-content,
    .ant-modal-body {
      overflow-x: hidden;
    }
    
    .ant-collapse-borderless {
      padding-top: 32px;
    }

    .jrPsht {
      padding-top: 32px;
    }
    .left {
      background-image: linear-gradient(to bottom, #f7f9fa, #ffffff);
    }
  }

  @media (min-width: 768px) and (max-width: 1024px) {
    &.ant-modal {
      border-radius: 12px;
      height: auto;
      margin: 16px auto;
      max-height: calc(100dvh - 32px);
      max-width: 930px;
      top: 0;
      width: calc(100vw - 80px) !important;
    }

    .ant-modal-content {
      border-radius: 12px;
      height: auto;
      min-height: 0;
      overflow: visible;
    }

    .ant-modal-close {
      color: #5d6b82;
      left: auto;
      right: 20px;
      top: 20px;
    }

    .ant-modal-body {
      border-radius: 12px;
      height: auto;
      max-height: calc(100dvh - 32px);
      overflow-x: hidden;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
    }

    .ant-modal-body > div {
      max-width: 100% !important;
      width: 100% !important;
    }
  }

  @media (min-width: 1025px) and (max-width: 1196px) {
    &.ant-modal {
      max-width: calc(100vw - 48px);
      width: calc(100vw - 48px) !important;
    }
  }

  @media (min-width: 1025px) {
    .ant-modal-body {
      background-image: linear-gradient(to bottom, #f7f9fa, #ffffff);
    }
  }

  .ant-modal-header {
    padding: 12px 64px 12px 32px;
  }

  .skeleton {
    display: flex;
    align-items: center;
  }

  .ant-tabs-ink-bar {
    bottom: 0px;
    top: 0px;
    height: 1px;
  }

  .ant-tabs-nav-wrap {
    background-color: #f7f9fa;
    height: 56px;
  }

  .ant-tabs-nav {
    background-color: #f7f9fa;

    .ant-tabs-tab {
      margin: 0px;
      height: 56px;
      padding-left: 24px;
      padding-right: 24px;
      line-height: 30px;
      border-right: 1px solid #e8e8e8;
      border-top: 1px solid #e8e8e8;

      &:hover {
        color: #343a40;
        transition: 0s;
      }
    }

    .ant-tabs-tab-active {
      font-weight: bold;
      color: black;
      border-right: 1px solid #e8e8e8;

      .anticon {
        color: #3d64ff;
      }
    }
  }

  .ant-tabs {
    background-image: linear-gradient(rgb(247, 249, 250), rgb(255, 255, 255));
    width: 100%;
    border: 1px solid #e8e8e8;
    border-top: 0px;

    .ant-tabs-tab-disabled {
      &:hover {
        color: #00000040;
      }
    }
  }

  .ant-tabs-bar a {
    color: currentColor;
    text-decoration: none;
  }

  .ant-modal-body {
    box-shadow: ${({ isSelected }) =>
      isSelected ? "0 1px 8px 1px #3D64FF" : ""};
    border-radius: 4px;
    padding: 24px 0;
    min-height: 50vh;
    transition: box-shadow 0.4s ease;
  }

  .ant-tabs-content {
    padding: 16px 0px 0px 0px;
  }

  .modal-arrow {
    display: flex;
    justify-content: center;
    align-items: center;

    position: fixed;
    top: calc(50% - ${arrowSize}px);
    color: white;
    border: 0;
    outline: 0;
    margin: 0;
    padding: 0;

    width: ${arrowSize}px;
    height: ${arrowSize}px;

    border-radius: 32px;

    background-color: #343a4050;
    font-size: 34px;
    cursor: pointer;
    transition: 0.15s ease background-color;

    &:hover {
      background-color: #343a40;
    }
    &.arrow-next {
      left: calc(50% + ${modalWidth}px / 2 + 16px);
    }
    &.arrow-prev {
      right: calc(50% + ${modalWidth}px / 2 + 16px);
    }
  }

  @media (max-width: 1024px) {
    .ant-tabs {
      border-left: 0;
      border-right: 0;
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
    }

    .ant-tabs-bar {
      flex-shrink: 0;
      margin: 0;
    }

    .ant-tabs-nav-container,
    .ant-tabs-nav-wrap,
    .ant-tabs-nav-scroll {
      height: 72px;
    }

    .ant-tabs-nav-container-scrolling {
      padding-left: 0;
      padding-right: 0;
    }

    .ant-tabs-tab-prev,
    .ant-tabs-tab-next {
      display: none;
    }

    .ant-tabs-nav-wrap {
      overflow: hidden;
      -webkit-overflow-scrolling: touch;
      width: 100%;
    }

    .ant-tabs-nav-scroll {
      max-width: 100%;
      overflow-x: auto;
      overflow-y: hidden;
      -webkit-overflow-scrolling: touch;
    }

    .ant-tabs-nav-scroll::-webkit-scrollbar {
      display: none;
    }

    .ant-tabs-nav {
      transform: none !important;
      display: inline-flex;
      flex-wrap: nowrap;
      min-width: max-content;
      white-space: nowrap;

      .ant-tabs-tab {
        align-items: center !important;
        display: inline-flex !important;
        flex: 0 0 auto;
        flex-direction: column !important;
        justify-content: center !important;
        height: 72px;
        line-height: 22px;
        min-width: 80px;
        padding: 12px 14px;
        text-align: center;
        vertical-align: top !important;
      }

      .ant-tabs-tab > div,
      .ant-tabs-tab > a {
        display: flex;
        flex-direction: column;
        gap: 4px;
        line-height: 18px;
      }

      .ant-tabs-tab > .ant-badge {
        display: block !important;
        height: 20px;
        line-height: 20px;
      }

      .ant-tabs-tab > .ant-badge + span {
        display: block !important;
        line-height: 18px;
        margin-top: 4px;
      }

      .ant-tabs-tab-active {
        transform: none !important;
      }

      .ant-tabs-tab .anticon {
        font-size: 20px;
      }
    }

    .ant-tabs-content {
      flex: 1;
      min-height: 0;
      overflow: visible;
      padding: 0;
    }

    .ant-tabs-tabpane,
    .ant-tabs-tabpane-active {
      height: auto;
      min-height: 0;
      overflow: visible;
    }

    .ant-tabs-tabpane[aria-hidden="false"] {
      display: block;
      min-height: 0;
      overflow: visible;
    }

    .ant-tabs-tabpane > .ant-row {
      margin-left: 0 !important;
      margin-right: 0 !important;
      width: 100%;
    }

    #messages .scrollable {
      padding: 24px 0;
    }

    #messages .message {
      line-height: 1.35;
      max-width: 82%;
      padding: 12px 16px;
    }

    textarea.message {
      border: 1px solid #dbe3ef !important;
      border-radius: 8px;
      color: #000d44;
      min-height: 56px;
      padding: 16px !important;
    }

    button.submit {
      border-radius: 8px !important;
      height: 56px !important;
      width: 56px !important;
    }

    .modal-arrow {
      display: none;
    }
  }

  @media (max-width: 560px) {
    .ant-tabs-nav-scroll {
      box-sizing: border-box;
      padding-left: 32px;
      padding-right: 32px;
      scroll-padding-left: 32px;
      scroll-padding-right: 32px;
      touch-action: pan-x;
      scrollbar-width: none;
    }

    .ant-tabs-nav-scroll::-webkit-scrollbar {
      display: none;
    }

    .ant-tabs-nav .ant-tabs-tab {
      min-width: 72px;
      padding-left: 10px;
      padding-right: 10px;
    }

    .ant-tabs-nav .ant-tabs-tab > div,
    .ant-tabs-nav .ant-tabs-tab > a,
    .ant-tabs-nav .ant-tabs-tab > .ant-badge + span {
      max-width: 104px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  @media (min-width: 561px) and (max-width: 767px) {
    .ant-tabs-nav-scroll {
      box-sizing: border-box;
      padding-left: 32px;
      padding-right: 32px;
      scroll-padding-left: 32px;
      scroll-padding-right: 32px;
      touch-action: pan-x;
    }
  }

  @media (min-width: 768px) and (max-width: 1024px) {
    .ant-tabs-bar {
      border-bottom: 1px solid #e5e9ed;
      margin: 0;
    }

    .ant-tabs-nav-container,
    .ant-tabs-nav-wrap,
    .ant-tabs-nav-scroll {
      height: 64px;
    }

    .ant-tabs-nav-wrap {
      background: #f7f9fa;
    }

    .ant-tabs-nav-scroll {
      text-align: center;
    }

    .ant-tabs-nav {
      background: #f7f9fa;

      .ant-tabs-tab-active {
        background: #ffffff;
      }
    }

    .ant-tabs-content {
      background: #ffffff;
      flex: 1;
      height: auto;
      min-height: 0;
      overflow: visible;
      padding-left: 128px;
    }
  }

  @media (min-width: 768px) and (max-width: 1024px) {
    .ant-tabs-bar {
      margin-left: 128px;
      width: calc(100% - 128px);
    }

    .ant-tabs-nav-scroll {
      text-align: left;
    }
  }

  @media (min-width: 768px) and (max-width: 865px) {
    .ant-tabs-nav-scroll {
      overflow-x: auto;
      overflow-y: hidden;
    }

    .ant-tabs-nav {
      display: inline-flex;
      flex-wrap: nowrap;
      min-width: max-content;
      white-space: nowrap;
    }

    .ant-tabs-nav .ant-tabs-tab {
      flex: 0 0 auto;
    }
  }
`;

const SkeletonContainer = styled.div`
  width: 100%;
  text-align: center;
  padding: 20px;
`;

const ModalContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  min-width: 0;

  /*
  > .left {
    width: 144px;
  }
  */

  > .top {
    width: 128px;
    background: ${({ theme, isRegistered }) =>
      !isRegistered && theme["@gray-30"]};
  }

  > .right {
    width: 1024px;
    background: ${({ theme, isRegistered }) =>
      !isRegistered && theme["@gray-30"]};
  }

  width: ${modalWidth}px;

  @media (min-width: 1025px) {
    width: 100%;

    > .top,
    > .left {
      width: 144px;
      background: transparent;
    }

    > .right {
      min-width: 0;
      width: calc(100% - 144px);
    }

    > .right.top {
      background: transparent;
    }

    > .right:not(.top) {
      background: #ffffff;
    }
  }

  @media (min-width: 1025px) and (max-width: 1196px) {
    width: 100%;

    > .left {
      width: 144px;
    }

    > .right {
      min-width: 0;
      min-height: max-content;
      width: calc(100% - 144px);
    }
  }

  @media (max-width: 1024px) {
    align-content: flex-start;
    flex: 1;
    flex-direction: row;
    flex-wrap: wrap;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: visible;
    max-width: 100vw;
    width: 100%;

    > .left.top {
      background: ${({ theme, isRegistered }) =>
        !isRegistered && theme["@gray-30"]};
      order: 1;
      width: 128px;

      .ant-avatar {
        height: 72px !important;
        margin-top: 40px !important;
        width: 72px !important;
      }
    }

    > .right.top {
      background: ${({ theme, isRegistered }) =>
        !isRegistered && theme["@gray-30"]};
      height: auto;
      min-height: max-content;
      min-width: 0;
      order: 2;
      width: calc(100% - 128px);
    }

    > .left:not(.top) {
      display: none;
    }

    > .right:not(.top) {
      display: flex;
      flex: 1 1 auto;
      height: auto;
      overflow: visible;
      min-width: 0;
      min-height: 0;
      order: 3;
      position: relative;
      width: 100%;
    }
  }

  @media (min-width: 768px) and (max-width: 1024px) {
    height: auto;
    max-width: 100%;
    min-width: 0;
    position: relative;
    width: 100%;

    > .left.top {
      height: 150px;
      width: 128px;
      background: transparent;

      .ant-avatar {
        height: 88px !important;
        margin-left: 28px !important;
        margin-top: 30px !important;
        width: 88px !important;
      }
    }

    > .right.top {
      min-height: max-content;
      min-width: 0;
      width: calc(100% - 128px);
    }

    > .left:not(.top) {
      border-right: 1px solid #eef2f7;
      display: block;
      bottom: 0;
      left: 0;
      order: 4;
      position: absolute;
      top: 214px;
      width: 128px;
      z-index: 2;
    }

    > .right:not(.top) {
      flex: 1 1 auto;
      height: auto;
      overflow: visible;
      min-width: 0;
      min-height: 0;
      order: 3;
      width: 100%;
    }
  }
`;

const SkeletonItem = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  animation: fadeIn 0.5s ease;
  overflow: hidden;
  margin-top: 20px;
  margin-left: 30px;

  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`;

const SkeletonInfo = styled.div`
  margin-left: 80px;
  display: flex;
  flex-direction: column;
  flex: 1 1 40%;
`;

const statusColor = {
  FINISHED: "#90DC9E",
  PENDING: "#EF7125",
  ERROR: "red",
  UNRECOGNIZED_ANSWER: "#5725ef"
};

const JobTitle = styled.div`
  font-size: 14px;
  color: #343a40;

  @media (max-width: 1024px) {
    color: #343a40;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.4;
    margin-top: 14px;
  }
`;

const CandidateDataContainer = styled.div`
  box-sizing: border-box;
  /*padding-top: 10px;*/
  padding-right: 21.5px;
  width: 1024px;

  @media (min-width: 1025px) and (max-width: 1196px) {
    width: 100%;
  }

  @media (max-width: 1024px) {
    position: relative;
    padding: 40px 16px 16px 0;
    width: 100%;
  }

  @media (max-width: 767px) {
    padding-right: 44px;
  }

  @media (min-width: 768px) and (max-width: 1024px) {
    padding: 10px 72px 18px 0;
  }

  @media (min-width: 768px) and (max-width: 899px) {
    padding-right: 96px;
  }
`;

const CandidateName = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 26px;
  color: #343a40;
  width: 100%;

  .referred-icon {
    display: inline-block;
    background: ${({ theme }) => theme["@primary"]};
    color: white;
    width: 30px;
    height: 30px;
    font-size: 18px;
    border-radius: 20px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
  }
  .name-row {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .name-row-span {
    display: block;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: wrap;
    width: 100%;
  }

  @media (max-width: 1024px) {
    align-items: flex-start;
    flex-direction: column;
    font-size: 24px;
    font-weight: 700;
    line-height: 1.2;

    .name-row {
      align-items: flex-start;
      flex-direction: column;
      gap: 8px;
      min-width: 0;
      width: 100%;
    }
  }

  @media (min-width: 768px) and (max-width: 1024px) {
    align-items: flex-start;
    flex-direction: row;
    gap: 16px;
    min-width: 0;

    .name-row {
      align-items: center;
      flex: 1 1 auto;
      flex-direction: row;
      flex-wrap: wrap;
      gap: 8px;
      min-width: 0;
    }
  }

  @media (min-width: 768px) and (max-width: 899px) {
    flex-direction: column;
    gap: 10px;

    .name-row {
      max-width: 100%;
      width: 100%;
    }
  }
`;

const LocationTitle = styled.div`
  font-size: 12px;
  color: #747d84;
  margin-top: 5px;

  .location-icon {
    display: none;
  }

  @media (max-width: 1024px) {
    align-items: flex-start;
    color: #747d84;
    display: flex;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.5;
    margin-top: 6px;

    .location-icon {
      color: #9aa6b8;
      display: inline-flex;
      flex: 0 0 auto;
      font-size: 16px;
      line-height: 1;
      margin-right: 8px;
      margin-top: 1px;
    }
  }

  @media (min-width: 768px) and (max-width: 1024px) {
    color: #747d84;
    font-size: 12px;
    margin-top: 6px;
  }
`;

const ActionsContainer = styled.div`
  padding-top: 10px;
  display: flex;
  flex-direction: column;

  .success-tag {
    width: fit-content;
    align-self: center;
    font-weight: bold;
    padding-inline: 15px;
  }

  .success-tag-label {
    font-size: 10px;
    font-weight: 600;
    overflow-wrap: normal;
    align-self: center;
    margin-top: 8px;
  }
`;

const Actions = styled.div`
  padding-top: 20px;
`;

const CandidateActions = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 85%% !important;

  @media (max-width: 1024px) {
    align-items: stretch;
    gap: 8px;
    margin-left: calc(-128px + 16px);
    margin-top: 16px;
    position: absolute;
    top: 176px;
    width: calc(100vw - 32px);

    button,
    .ant-btn {
      align-items: center;
      border-width: 1.5px;
      display: inline-flex;
      flex: 1;
      font-size: 11px !important;
      height: 56px !important;
      justify-content: center;
      margin-right: 0 !important;
      min-width: 0;
      overflow: hidden;
      padding: 0 8px !important;
      white-space: wrap;
    }

    .anticon {
      flex-shrink: 0;
      font-size: 11px;
      margin-right: 4px;
    }

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: wrap;
    }
  }

  @media (min-width: 768px) and (max-width: 1024px) {
    align-items: flex-end;
    flex: 0 0 auto;
    gap: 8px;
    margin-left: 0;
    margin-top: 0;
    position: static;
    width: auto;

    button,
    .ant-btn {
      flex: 0 0 auto;
      font-size: 10px !important;
      height: 24px !important;
      min-width: 146px;
      padding: 0 14px !important;
    }
  }

  @media (max-width: 767px) {
    display: none;
  }
`;

const MobileCvActions = styled.div`
  display: none;

  @media (max-width: 767px) {
    border-top: 1px solid #eef2f7;
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-left: -112px;
    margin-top: 18px;
    padding: 18px 16px 0;
    width: calc(100vw - 32px);

    .mobile-cv-action {
      align-items: center;
      background: #ffffff;
      border: 1px solid #e4e8f0;
      border-radius: 12px;
      box-shadow: 0 2px 6px rgba(15, 38, 87, 0.04);
      color: #2864f0;
      display: flex;
      font-size: 12px;
      font-weight: 600;
      height: 44px;
      justify-content: center;
      line-height: 18px;
      padding: 0 16px;
      text-decoration: none;
      width: 100%;
    }

    .mobile-cv-action:first-child {
      background: #f7f9ff;
    }

    .mobile-cv-action:disabled {
      cursor: not-allowed;
      opacity: 0.65;
    }

    .mobile-cv-action .anticon {
      font-size: 16px;
      margin-right: 10px;
    }
  }
`;

const MobileActions = styled.div`
  display: none;

  @media (max-width: 767px) {
    display: block;
    position: absolute;
    right: 96px;
    top: 0;
    z-index: 3;

    .mobile-actions-trigger {
      background: transparent;
      border: 0;
      color: #000d44;
      font-size: 24px;
      height: 40px;
      padding: 0;
      width: 40px;
    }
  }
`;

const MobileHeaderCheckbox = styled(Checkbox)`
  display: none !important;

  @media (max-width: 767px) {
    align-items: center;
    display: flex !important;
    height: 40px;
    justify-content: center;
    padding: 0;
    position: absolute;
    right: 56px;
    top: 0;
    width: 40px;
    z-index: 3;

    .ant-checkbox {
      margin: 0;
    }

    .ant-checkbox-inner {
      height: 21px;
      width: 21px;

      :after {
        margin-left: 2px;
      }
    }
  }
`;

const MobileClose = styled.button`
  display: none;

  @media (max-width: 1024px) {
    background: transparent;
    border: 0;
    color: #004cff;
    display: block;
    font-size: 24px;
    height: 40px;
    right: 16px; //left
    padding: 0;
    position: absolute;
    top: 0px; //88
    width: 40px;
    z-index: 3;
  }
`;

const MobileActionsContent = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 180px;

  > * {
    border-bottom: 1px solid #e5e9ed;
    padding: 8px 0;
  }

  > *:last-child {
    border-bottom: 0;
  }
`;

const MobileTabControls = styled.div`
  display: none;
  @media (max-width: 1024px) {
    display: block;
    position: absolute;
    pointer-events: none;
    z-index: 4;

    button {
      position: absolute;
      top: 0;
      bottom: 0;
      width: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 0;
      background: #f7f9fa;
      color: #004cff;
      cursor: pointer;
      pointer-events: auto;
    }
    button[data-direction="prev"] {
      left: 0;
    }
    button[data-direction="next"] {
      right: 0;
    }
    button[hidden] {
      display: none;
    }
    button:focus-visible {
      outline: 2px solid #004cff;
      outline-offset: -3px;
    }
  }
`;

const CandidateTabsPanel = styled.div`
  position: relative;
  min-width: 0;

  @media (max-width: 767px) {
    box-sizing: border-box;
    max-width: 100%;

    > .ant-tabs,
    > .ant-tabs > .ant-tabs-bar {
      box-sizing: border-box;
      min-width: 0;
      max-width: 100%;
    }
  }

  @media (max-width: 1024px) {
    > .ant-tabs > .ant-tabs-bar .ant-tabs-nav-container {
      padding-left: 40px;
      padding-right: 40px;
      padding-left: calc(40px + var(--tabs-clipped-left, 0px));
      padding-right: calc(40px + var(--tabs-clipped-right, 0px));
    }
    > .ant-tabs > .ant-tabs-bar .ant-tabs-nav-scroll {
      padding-left: 0;
      padding-right: 0;
      scroll-padding-left: 0;
      scroll-padding-right: 0;
      overflow-x: auto;
      overflow-y: hidden;
      scrollbar-width: none;
    }
    > .ant-tabs > .ant-tabs-bar .ant-tabs-nav {
      display: inline-flex;
      flex-wrap: nowrap;
      min-width: max-content;
      transform: none !important;
    }
    > .ant-tabs > .ant-tabs-bar .ant-tabs-tab {
      flex: 0 0 auto;
    }
  }

  @media (min-width: 523px) and (max-width: 644px) {
    > .ant-tabs > .ant-tabs-bar .ant-tabs-nav-container {
      box-sizing: border-box;
      width: 100%;
      padding-left: calc(40px + var(--tabs-clipped-left, 0px)) !important;
      padding-right: calc(40px + var(--tabs-clipped-right, 0px)) !important;
    }

    > .ant-tabs > .ant-tabs-bar .ant-tabs-nav-wrap {
      box-sizing: border-box;
      width: 100%;
      margin-left: 0 !important;
      margin-right: 0 !important;
      overflow: hidden;
    }

    > .ant-tabs > .ant-tabs-bar .ant-tabs-nav-scroll {
      box-sizing: border-box;
      width: 100%;
      max-width: 100%;
      padding-left: 0 !important;
      padding-right: 0 !important;
    }

    > .ant-tabs > .ant-tabs-bar .ant-tabs-tab-prev,
    > .ant-tabs > .ant-tabs-bar .ant-tabs-tab-next {
      display: none !important;
    }
  }
`;

const SelectedCheckbox = styled(Checkbox)`
  .ant-checkbox {
    .ant-checkbox-inner {
      height: 21px;
      width: 21px;
      :after {
        margin-left: 2px;
      }
    }
    margin-top: -3px;
    margin-right: 1px;
  }

  @media (max-width: 767px) {
    display: none;
  }
`;

const tagsStyle = {
  verticalAlign: "middle",
  height: 30,
  borderRadius: 15,
  paddingTop: 4,
  paddingLeft: 16,
  paddingRight: 16,
  marginLeft: 9,
  marginRight: 0
};

const HeaderTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  min-width: fit-content;
  margin: 1rem 0;

  @media (max-width: 1024px) {
    gap: 6px;
  }
`;

const HeaderTag = styled(Tag)`
  height: 16px;
  line-height: 14px;
  border-radius: 8px;
  border: solid 1px #e5e9ed;
  background-color: #ffffff;
  padding: 0 9px;
  font-size: 10px;
  color: #747d84;
  text-transform: uppercase;
  margin: 0;
`;

const RecentIcon = styled(Icon)`
  position: absolute;
  left: 90px;
  top: 30px;
  z-index: 10;
  font-size: 24px;
`;

const DocumentSubmissionsContainer = styled.div`
  padding: 8px 16px 16px;

  .document-submission-summary {
    align-items: center;
    display: flex;
    justify-content: space-between;
    margin-bottom: 18px;
  }

  .document-submission-summary__text {
    color: #53627c;
    font-size: 14px;
  }

  .document-submission-progress {
    align-items: center;
    display: flex;
    gap: 14px;
    min-width: 320px;
  }

  .document-submission-progress__label {
    color: #000d44;
    font-size: 13px;
    font-weight: 700;
    white-space: wrap;
  }

  .document-submission-table {
    border: 1px solid #e8e8e8;
    border-radius: 4px;
    overflow: hidden;
  }

  .document-submission-row {
    align-items: center;
    display: grid;
    grid-template-columns: 2fr 1fr 1.2fr 1.3fr 1.4fr;
    padding: 14px 24px;
  }

  .document-submission-row--header {
    background: #f7f9fa;
    color: #53627c;
    font-size: 13px;
    font-weight: 600;
  }

  .document-submission-file {
    align-items: center;
    display: flex;
    gap: 14px;
  }

  .document-submission-file__icon {
    align-items: center;
    background: #fff1f1;
    border-radius: 8px;
    color: #ff4d4f;
    display: flex;
    font-size: 24px;
    height: 48px;
    justify-content: center;
    width: 48px;
  }

  .document-submission-file__name {
    color: #004cff;
    font-weight: 700;
  }

  .document-submission-file__meta {
    color: #53627c;
    font-size: 13px;
    margin-top: 4px;
  }

  .document-submission-actions {
    display: flex;
    gap: 10px;
  }

  .document-submission-action {
    align-items: center;
    background: #f1f5ff;
    border: 1px solid #e5e9ed;
    border-radius: 50%;
    color: #004cff;
    display: inline-flex;
    font-size: 18px;
    height: 40px;
    justify-content: center;
    width: 40px;
  }

  .document-submission-action:hover {
    background: #e8efff;
    color: #004cff;
  }

  .document-submission-status {
    align-items: center;
    background: #e5f8ed;
    border-radius: 16px;
    color: #00a85a;
    display: inline-flex;
    gap: 6px;
    font-size: 13px;
    font-weight: 700;
    padding: 6px 12px;
    width: fit-content;
  }
`;

const formatFileSize = size => {
  if (!size) return "";
  const mb = size / 1024 / 1024;
  return `${mb >= 1 ? mb.toFixed(1) : Math.ceil(size / 1024)} ${
    mb >= 1 ? "MB" : "KB"
  }`;
};

const formatUploadDate = timestamp =>
  timestamp ? moment(Number(timestamp)).format("D MMM. YYYY, HH:mm") : "-";

const getDocumentFiles = document => {
  if (document.uploadMode === "front_back") {
    return [
      { label: "front", file: R.path(["currentFiles", "front"], document) },
      { label: "back", file: R.path(["currentFiles", "back"], document) }
    ];
  }

  return document.currentFile && document.currentFile.url
    ? [{ label: "file", file: document.currentFile }]
    : [];
};

const getDocumentRows = documents =>
  R.chain(document => {
    const files = getDocumentFiles(document);

    if (document.uploadMode === "front_back") {
      return files.map(({ label, file }) => ({
        document,
        file,
        label,
        rowKey: `${document.documentRequirementId}-${label}`,
        status: file && file.url ? "completed" : "pending"
      }));
    }

    return [
      {
        document,
        file: files[0] && files[0].file,
        label: "file",
        rowKey: document.documentRequirementId,
        status: document.status || document.submissionStatus
      }
    ];
  }, documents);

const CandidateDocumentSubmissions = ({ submissions = [], t }) => {
  const documents = R.chain(
    submission =>
      R.map(
        document => ({
          ...document,
          submissionStatus: submission.status
        }),
        R.propOr([], "documents", submission)
      ),
    submissions
  );

  if (!documents.length) return null;

  const completedDocuments = documents.filter(
    document => (document.status || document.submissionStatus) === "completed"
  ).length;
  const completionPercentage = Math.round(
    (completedDocuments / documents.length) * 100
  );
  const documentRows = getDocumentRows(documents);

  return (
    <DocumentSubmissionsContainer>
      <div className="document-submission-summary">
        <div className="document-submission-summary__text">
          {t("opportunity:process.candidateDetails.documents.description")}
        </div>
        <div className="document-submission-progress">
          <span className="document-submission-progress__label">
            {t("opportunity:process.candidateDetails.documents.progress", {
              completed: completedDocuments,
              total: documents.length
            })}
          </span>
          <Progress
            percent={completionPercentage}
            showInfo={false}
            strokeColor="#13c27f"
            strokeWidth={8}
          />
          <span className="document-submission-progress__label">
            {completionPercentage}%
          </span>
        </div>
      </div>
      <div className="document-submission-table">
        <div className="document-submission-row document-submission-row--header">
          <span>
            {t(
              "opportunity:process.candidateDetails.documents.columns.document"
            )}
          </span>
          <span>
            {t(
              "opportunity:process.candidateDetails.documents.columns.required"
            )}
          </span>
          <span>
            {t("opportunity:process.candidateDetails.documents.columns.status")}
          </span>
          <span>
            {t(
              "opportunity:process.candidateDetails.documents.columns.uploadedAt"
            )}
          </span>
          <span>
            {t(
              "opportunity:process.candidateDetails.documents.columns.actions"
            )}
          </span>
        </div>
        {documentRows.map(({ document, file, label, rowKey, status }) => {
          const fileExtension = file && file.extension;
          const fileSize = file && formatFileSize(file.size);
          const partLabel =
            label !== "file"
              ? t(
                  `opportunity:process.candidateDetails.documents.parts.${label}`
                )
              : null;

          return (
            <div className="document-submission-row" key={rowKey}>
              <div className="document-submission-file">
                <div className="document-submission-file__icon">
                  <Icon type="file-pdf" />
                </div>
                <div>
                  <div className="document-submission-file__name">
                    {partLabel
                      ? `${document.name} - ${partLabel}`
                      : document.name}
                  </div>
                  <div className="document-submission-file__meta">
                    {[fileExtension && fileExtension.toUpperCase(), fileSize]
                      .filter(Boolean)
                      .join(" · ") || "-"}
                  </div>
                </div>
              </div>
              <div>
                <Tag color={document.required ? "blue" : "default"}>
                  {document.required
                    ? t(
                        "opportunity:process.candidateDetails.documents.required"
                      )
                    : t(
                        "opportunity:process.candidateDetails.documents.optional"
                      )}
                </Tag>
              </div>
              <div>
                <span className="document-submission-status">
                  <Icon
                    type={
                      status === "completed" ? "check-circle" : "clock-circle"
                    }
                  />
                  {t(
                    `opportunity:process.candidateDetails.documents.status.${status}`,
                    { defaultValue: status }
                  )}
                </span>
              </div>
              <div>{formatUploadDate(file && file.uploadedAt)}</div>
              <div className="document-submission-actions">
                {file && file.url && (
                  <Tooltip
                    title={t(
                      "opportunity:process.candidateDetails.documents.actions.download"
                    )}
                  >
                    <a
                      className="document-submission-action"
                      download={file.fileName}
                      href={file.url}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      <Icon type="download" />
                    </a>
                  </Tooltip>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </DocumentSubmissionsContainer>
  );
};

const CandidateModal = ({
  candidate,
  onClose,
  actions,
  videoMetadata,
  loading,
  t,
  tag,
  tags = [],
  activity,
  chatbotStatus,
  chatbotTags = [],
  chatTab,
  history,
  match,
  sentiment,
  hasTests,
  screening,
  notes,
  steps,
  stepsOrder,
  onNext,
  onPrev,
  onSelect,
  isSelected,
  opportunityId,
  suggestedList,
  list,
  onRequestCvUpdate,
  requestCvUpdateStatus = "initial",
  showRequestCvUpdateButton = false,
  companyId
}) => {
  const tabsPanelRef = useRef(null);
  const [tabNavigation, setTabNavigation] = useState({
    prev: false,
    next: false,
    top: 0,
    left: 0,
    width: 0,
    height: 0
  });
  const theme = useContext(ThemeContext);
  const [currentTab, setCurrentTab] = useState(
    R.pathOr("info", ["params", "tab"], match)
  );

  const [disabledMove, setDisabled] = useState(false);
  const [isMobileActionsOpen, setMobileActionsOpen] = useState(false);
  const [documentSubmissions, setDocumentSubmissions] = useState([]);

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      !window.matchMedia("(max-width: 1024px)").matches
    ) {
      return undefined;
    }

    const previousBodyOverflow = document.body.style.overflow;
    const previousBodyOverflowX = document.body.style.overflowX;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const previousHtmlOverflowX = document.documentElement.style.overflowX;

    document.body.style.overflow = "hidden";
    document.body.style.overflowX = "hidden";
    document.documentElement.style.overflow = "hidden";
    document.documentElement.style.overflowX = "hidden";

    window.scrollTo(0, 0);

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.body.style.overflowX = previousBodyOverflowX;
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.documentElement.style.overflowX = previousHtmlOverflowX;
    };
  }, []);

  const {
    avatar,
    fullname,
    first,
    last,
    country,
    province = {},
    district = {},
    answers = [],
    cv,
    experience,
    score = 0
  } = candidate;

  const provinceName = province?.name;
  const districtName = district?.name;
  const addressInfo = useMemo(
    () =>
      formatAddress({
        country: getCountry(country),
        province: provinceName,
        district: districtName
      }),
    [country, provinceName, districtName]
  );

  const recentCandidateData = R.find(
    R.propEq("id", candidate.id),
    suggestedList
  );

  const applyTime = useMemo(
    () =>
      recentCandidateData &&
      recentCandidateData.opportunityProcessStatus &&
      recentCandidateData.opportunityProcessStatus.length &&
      recentCandidateData.opportunityProcessStatus[0].applyTime,
    [recentCandidateData]
  );

  const isRecentCandidate =
    recentCandidateData && isRecent(applyTime) && opportunityId;

  const hasRank = useSelector(state => {
    let rank = R.pathOr(undefined, ["company", "services", "rank"], state);
    if (rank === undefined) {
      rank = R.pathOr(false, ["headquarter", "services", "rank"], state);
    }
    return rank;
  });

  const hasScore = useMemo(() => hasRank && score > 0, [hasRank, score]);

  useEffect(() => {
    let isMounted = true;

    if (!companyId || !opportunityId || !candidate.id) {
      setDocumentSubmissions([]);
      return undefined;
    }

    api.opportunity.candidates
      .fetchDocumentSubmissions(companyId, opportunityId, candidate.id)
      .then(submissions => {
        if (isMounted) setDocumentSubmissions(submissions || []);
      })
      .catch(() => {
        if (isMounted) setDocumentSubmissions([]);
      });

    return () => {
      isMounted = false;
    };
  }, [candidate.id, companyId, opportunityId]);

  const hasDocumentSubmissions = useMemo(
    () =>
      R.any(
        submission => R.propOr([], "documents", submission).length > 0,
        documentSubmissions
      ),
    [documentSubmissions]
  );

  const hasSuccessProbabilityTest = useSelector(state => {
    let res = R.pathOr(
      undefined,
      ["company", "services", "successProbabilityTest"],
      state
    );
    if (res === undefined) {
      res = R.pathOr(
        false,
        ["headquarter", "services", "successProbabilityTest"],
        state
      );
    }
    return res;
  });

  const tagProfile = useSelector(state => {
    if (!hasSuccessProbabilityTest) return;
    const op = R.path(["opportunity", "selected"], state);
    if (!op || R.isEmpty(op)) return;
    return R.propOr(0, "successProbabilityProfile", op);
  });

  const successProbabilityTag = useMemo(() => {
    if (tagProfile === undefined) return;
    const tests = R.propOr([], "tests", candidate);
    const test =
      R.find(
        R.both(R.propEq("source", "worcket"), R.propEq("testIdentifier", "3")),
        tests
      ) || {};
    const result =
      R.find(
        R.propEq("idProfile", tagProfile),
        test.serviceCallResultData || []
      ) || {};
    return result.tag;
  }, [candidate, tagProfile]);

  const currentPosition = useMemo(() => {
    return R.pipe(
      R.propOr([], "lastPositions"),
      R.find(R.prop("current"))
    )(candidate);
  }, [candidate]);

  const lastPosition = useMemo(() => {
    return R.pipe(
      R.propOr([], "lastPositions"),
      R.sortBy(R.prop("end")),
      R.last
    )(candidate);
  }, [candidate]);

  const validTabs = [
    "info",
    "notas",
    "chat",
    "sentimiento",
    "posiciones",
    "preguntas",
    "tests",
    "history"
  ];

  const setTab = useCallback(
    (key, updateUrl = true) => {
      const { candidateId } = match.params;
      if (updateUrl) {
        history.push(
          match.url.replace(
            new RegExp(`/${candidateId}(/.+|)`),
            `/${candidateId}/${key}`
          )
        );
      }

      setCurrentTab(key);
    },
    [history, match.params, match.url]
  );

  useEffect(() => {
    const { tab } = match.params;
    setTab(validTabs.includes(tab) ? tab : "info", false);
  }, [match.params, validTabs, setTab]);

  useEffect(() => {
    const panel = tabsPanelRef.current;
    if (!panel) return undefined;
    const bar = panel.querySelector(".ant-tabs-bar");
    const viewport = bar && bar.querySelector(".ant-tabs-nav-scroll");
    const nav = bar && bar.querySelector(".ant-tabs-nav");
    if (!viewport || !nav) return undefined;
    let frame;
    let disposed = false;
    const update = () => {
      const barRect = bar.getBoundingClientRect();
      const panelRect = panel.getBoundingClientRect();
      // Keep the controls inside clipping ancestors, including the modal scrollbar.
      let visibleLeft = Math.max(0, barRect.left);
      let visibleRight = Math.min(
        document.documentElement.clientWidth,
        barRect.right
      );
      for (
        let ancestor = bar.parentElement;
        ancestor;
        ancestor = ancestor.parentElement
      ) {
        const overflowX = window.getComputedStyle(ancestor).overflowX;
        if (/^(auto|scroll|hidden|clip)$/.test(overflowX)) {
          const rect = ancestor.getBoundingClientRect();
          const left = rect.left + ancestor.clientLeft;
          visibleLeft = Math.max(visibleLeft, left);
          visibleRight = Math.min(visibleRight, left + ancestor.clientWidth);
        }
      }
      const clippedLeft = Math.max(0, visibleLeft - barRect.left);
      const clippedRight = Math.max(0, barRect.right - visibleRight);
      // Reserve the button gutters in the visible part of the bar as well.
      panel.style.setProperty("--tabs-clipped-left", `${clippedLeft}px`);
      panel.style.setProperty("--tabs-clipped-right", `${clippedRight}px`);
      const max = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      const position = Math.max(0, Math.min(max, viewport.scrollLeft));
      const mobile = window.matchMedia("(max-width: 1024px)").matches;
      const next = {
        prev: mobile && position > 1,
        next: mobile && max - position > 1,
        top: barRect.top - panelRect.top - panel.clientTop + panel.scrollTop,
        left:
          visibleLeft - panelRect.left - panel.clientLeft + panel.scrollLeft,
        width: Math.max(0, visibleRight - visibleLeft),
        height: barRect.height
      };
      setTabNavigation(previous =>
        Object.keys(next).every(key => previous[key] === next[key])
          ? previous
          : next
      );
    };
    const schedule = () => {
      if (disposed) return;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };
    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(schedule)
        : null;
    [panel, bar, viewport, nav].forEach(element => {
      if (resizeObserver) resizeObserver.observe(element);
    });
    const mutationObserver = new MutationObserver(schedule);
    mutationObserver.observe(nav, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true
    });
    viewport.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    if (document.fonts) document.fonts.ready.then(schedule);
    schedule();
    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      viewport.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (resizeObserver) resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [loading]);

  const scrollMobileTabs = useCallback(direction => {
    const panel = tabsPanelRef.current;
    const viewport =
      panel && panel.querySelector(".ant-tabs-bar .ant-tabs-nav-scroll");
    if (!viewport) return;
    const distance = Math.max(1, viewport.clientWidth * 0.75);
    viewport.scrollBy({
      left: direction === "next" ? distance : -distance,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth"
    });
  }, []);

  // Arrow right listener
  useKeyPress(
    ["Right", "ArrowRight"],
    () => !disabledMove && !loading && onNext && onNext()
  );

  // Arrow left listener
  useKeyPress(
    ["Left", "ArrowLeft"],
    () => !disabledMove && !loading && onPrev && onPrev()
  );

  // Close modal with escape
  useKeyPress(["Escape"], onClose);

  const isReferred =
    answers &&
    R.find(
      R.both(
        R.propEq("type", "referral"),
        R.pathEq(["answer", "wasReferred"], true)
      ),
      answers
    );

  const hasChatbotTags = useMemo(
    () =>
      R.compose(
        R.not,
        R.isEmpty,
        R.flatten,
        R.map(R.prop("chatbotTags")),
        R.defaultTo([])
      )(chatbotTags),
    [chatbotTags]
  );

  const renderSideActions = () => (
    <>
      {R.includes(list, [CANDIDATE_LISTS.suggested, CANDIDATE_LISTS.hunting]) &&
        (hasScore ? (
          <MatchPercentage score={score} isModal />
        ) : (
          <>
            {applyTime ? (
              <Date
                isModal
                value={applyTime}
                fontSizeLabel={9}
                fontSizeDay={24}
                fontSizeMonth={10}
                label={t("opportunity:candidates.postulationDateLabel")}
                highlighted={isRecentCandidate}
              />
            ) : (
              <div />
            )}
          </>
        ))}
      {successProbabilityTag && (
        <>
          <SuccessProbabilityTag
            tag={successProbabilityTag}
            tooltip={false}
            className="success-tag"
          />
          <span className="success-tag-label">
            {t(
              "opportunity:process.candidateDetails.successProbability-worcket.title"
            )}
          </span>
        </>
      )}
      <Actions>{actions}</Actions>
    </>
  );

  return (
    <>
      <MobileModalGlobalStyle />
      <StyledModal
        isSelected={isSelected}
        closable={!Boolean(onSelect)}
        wrapProps={{ "data-test": "candidate-modal" }}
        visible={true}
        width={modalWidth}
        zIndex={candidateModalZIndex}
        onCancel={!disabledMove && onClose}
        footer={null}
      >
        {loading ? (
          <SkeletonContainer>
            <SkeletonItem>
              <Skeleton
                active
                avatar
                title={{ width: 350, rows: 1 }}
                paragraph={false}
              />
            </SkeletonItem>
            <SkeletonInfo>
              <SkeletonItem>
                <Skeleton
                  active
                  avatar={false}
                  title={{ width: 120, rows: 6 }}
                  paragraph={{ rows: 5 }}
                />
              </SkeletonItem>
              <SkeletonItem>
                <Skeleton
                  active
                  avatar={false}
                  title={{ width: 120, rows: 6 }}
                  paragraph={{ rows: 5 }}
                />
              </SkeletonItem>
            </SkeletonInfo>
          </SkeletonContainer>
        ) : (
          <div style={{ position: "relative" }}>
            {onClose && !disabledMove && (
              <MobileClose type="button" onClick={onClose}>
                <Icon type="close" />
              </MobileClose>
            )}
            {onSelect && (
              <MobileHeaderCheckbox
                checked={isSelected}
                data-test="select-candidate-checkbox-mobile"
                disabled={false}
                onChange={event => onSelect(event.target.checked)}
              />
            )}
            <MobileActions>
              <Popover
                visible={isMobileActionsOpen}
                onVisibleChange={setMobileActionsOpen}
                getPopupContainer={triggerNode => triggerNode.parentNode}
                overlayStyle={{ zIndex: candidateModalZIndex + 1 }}
                content={
                  <MobileActionsContent
                    onClick={() => setMobileActionsOpen(false)}
                  >
                    <ActionsContainer>{renderSideActions()}</ActionsContainer>
                  </MobileActionsContent>
                }
                placement="bottomRight"
                trigger="click"
              >
                <button
                  className="mobile-actions-trigger"
                  type="button"
                  onClick={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    setMobileActionsOpen(open => !open);
                  }}
                >
                  <Icon type="more" />
                </button>
              </Popover>
            </MobileActions>
            {onPrev && !disabledMove && (
              <button
                title={t("opportunity:candidates.modal.goToPrevious")}
                data-test="modal-arrow-prev"
                className="modal-arrow arrow-prev"
                onClick={onPrev}
              >
                <Icon type="left" />
              </button>
            )}
            {onNext && !disabledMove && (
              <button
                title={t("opportunity:candidates.modal.goToNext")}
                className="modal-arrow arrow-next"
                data-test="modal-arrow-next"
                onClick={onNext}
              >
                <Icon type="right" />
              </button>
            )}
            <ModalContainer isRegistered={candidate.isRegistered}>
              <div className="left top">
                <Tooltip
                  placement="top"
                  title={
                    list === CANDIDATE_LISTS.suggested &&
                    hasScore &&
                    applyTime &&
                    moment(applyTime).format("LLL")
                  }
                >
                  <Avatar
                    size={80}
                    style={{
                      marginTop: 32,
                      backgroundColor: theme["@gray-50"],
                      marginLeft: 31,
                      fontSize: 30
                    }}
                    src={avatar && getOptimizedAvatar(avatar)}
                  >
                    {!R.isEmpty(candidate) && initials(candidate)}
                  </Avatar>
                </Tooltip>
                {isRecentCandidate && (
                  <Tooltip
                    placement="top"
                    title={t("opportunity:candidates.recentPostulationTooltip")}
                  >
                    <RecentIcon
                      type="check-circle"
                      theme="filled"
                      style={{
                        color: theme["@primary"],
                        background: "white",
                        borderRadius: "50%"
                      }}
                    />
                  </Tooltip>
                )}
              </div>
              <div className="right top">
                {!loading && (
                  <div style={{ maxWidth: "88%" }}>
                    <CandidateActions>
                      {showRequestCvUpdateButton && (
                        <Button
                          style={{ marginRight: 12 }}
                          key="request-cv-update"
                          icon="mail"
                          size="sm"
                          loading={requestCvUpdateStatus === "loading"}
                          disabled={requestCvUpdateStatus === "resolved"}
                          onClick={onRequestCvUpdate}
                        >
                          {t("opportunity:candidates.requestCvUpdate")}
                        </Button>
                      )}
                      {cv && (
                        <Button
                          style={{ marginRight: 12 }}
                          key="download-cv"
                          icon="download"
                          size="sm"
                        >
                          <a
                            href={cv}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {t("opportunity:candidates.downloadCv")}
                          </a>
                        </Button>
                      )}
                      {onSelect && (
                        <SelectedCheckbox
                          checked={isSelected}
                          data-test="select-candidate-checkbox"
                          disabled={false}
                          onChange={event => onSelect(event.target.checked)}
                        ></SelectedCheckbox>
                      )}
                    </CandidateActions>
                    <CandidateDataContainer>
                      <CandidateName>
                        <div className="name-row">
                          <div className="name-row-span">
                            <span style={{ marginRight: 15 }}>
                              {fullname || `${first} ${last}`}
                            </span>
                            {isReferred && (
                              <Tooltip
                                placement="top"
                                title={t(
                                  "opportunity:process.candidateDetails.referral"
                                )}
                              >
                                <div className="referred-icon">
                                  <Icon type="usergroup-add" />
                                </div>
                              </Tooltip>
                            )}
                          </div>
                        </div>
                      </CandidateName>
                      {(experience ||
                        R.prop("title", currentPosition) ||
                        R.prop("title", lastPosition)) && (
                        <JobTitle>
                          {currentPosition
                            ? currentPosition.title
                            : lastPosition
                            ? lastPosition.title
                            : experience === 0
                            ? t(
                                "opportunity:process.candidateDetails.experience.noExperience"
                              )
                            : `${t(
                                `catalogs:experience.${experience
                                  .toString()
                                  .replace(".", ",")}`
                              )} ${t(
                                "opportunity:process.candidateDetails.experience.years"
                              )}`}
                        </JobTitle>
                      )}
                      <LocationTitle>
                        <Icon className="location-icon" type="environment" />
                        <span>{addressInfo}</span>
                      </LocationTitle>
                      <HeaderTags>
                        {tag && (
                          <Tag
                            style={tagsStyle}
                            data-test="candidate-modal-tag"
                          >
                            {tag}
                          </Tag>
                        )}
                        {R.flatten(tags).map((headerTag, index) => (
                          <HeaderTag
                            key={`${headerTag}-${index}`}
                            title={headerTag}
                            data-test="candidate-modal-header-tag"
                          >
                            {headerTag}
                          </HeaderTag>
                        ))}
                        {screening && screening.screeningText && (
                          <Tag
                            style={{
                              ...tagsStyle,
                              color: screening.screeningTextColor
                            }}
                            color={screening.screeningColor}
                          >
                            {screening.screeningText}
                          </Tag>
                        )}
                        {chatbotStatus && (
                          <Tag
                            color={statusColor[chatbotStatus]}
                            style={tagsStyle}
                          >
                            {t(
                              `opportunity:process.stepTypes.chatbot.chatbotStatus.${chatbotStatus}`
                            )}
                          </Tag>
                        )}
                        {!candidate.isRegistered && (
                          <Tag
                            color={theme["@gray-40"]}
                            style={{
                              ...tagsStyle,
                              color: theme["@gray-60"]
                            }}
                          >
                            <span style={{ fontSize: 12 }}>
                              {t("global:otherCandidate")}
                            </span>
                          </Tag>
                        )}
                      </HeaderTags>
                      <MobileCvActions>
                        {showRequestCvUpdateButton && (
                          <button
                            className="mobile-cv-action"
                            type="button"
                            disabled={requestCvUpdateStatus === "resolved"}
                            onClick={onRequestCvUpdate}
                          >
                            <Icon type="mail" />
                            {t("opportunity:candidates.requestCvUpdate")}
                          </button>
                        )}
                        {cv && (
                          <a
                            className="mobile-cv-action"
                            href={cv}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Icon type="download" />
                            {t("opportunity:candidates.downloadCv")}
                          </a>
                        )}
                      </MobileCvActions>
                    </CandidateDataContainer>
                  </div>
                )}
              </div>

              <div className="left">
                {!loading && (
                  <ActionsContainer>{renderSideActions()}</ActionsContainer>
                )}
              </div>

              <CandidateTabsPanel className="right" ref={tabsPanelRef}>
                <MobileTabControls
                  style={{
                    top: tabNavigation.top,
                    left: tabNavigation.left,
                    width: tabNavigation.width,
                    height: tabNavigation.height
                  }}
                >
                  <button
                    type="button"
                    data-direction="prev"
                    aria-label="Desplazar pestañas hacia la izquierda"
                    hidden={!tabNavigation.prev}
                    onClick={() => scrollMobileTabs("prev")}
                  >
                    <Icon type="left" />
                  </button>
                  <button
                    type="button"
                    data-direction="next"
                    aria-label="Desplazar pestañas hacia la derecha"
                    hidden={!tabNavigation.next}
                    onClick={() => scrollMobileTabs("next")}
                  >
                    <Icon type="right" />
                  </button>
                </MobileTabControls>
                <Tabs
                  defaultActiveKey={currentTab}
                  activeKey={currentTab}
                  onChange={setTab}
                  animated={false}
                >
                  <Tabs.TabPane
                    tab={
                      <div data-test="candidate-modal-information-tab">
                        <Icon type="user" />
                        {t("opportunity:process.candidateDetails.information")}
                      </div>
                    }
                    key={`info`}
                    activeKey={`info`}
                  >
                    <Row gutter={16} style={{ marginLeft: 0, marginRight: 16 }}>
                      <Details
                        opportunityId={opportunityId}
                        candidate={candidate}
                        videoMetadata={videoMetadata || candidate.videoMetadata}
                      />
                      {activity}
                      {typeof notes === "function" && candidate.isRegistered
                        ? notes({ isActive: currentTab === "info" })
                        : null}
                    </Row>
                  </Tabs.TabPane>
                  <Tabs.TabPane
                    tab={
                      <div data-test="candidate-modal-notes-tab">
                        <Icon type="edit" />
                        {t("opportunity:process.candidateDetails.notes")}
                      </div>
                    }
                    key={`notas`}
                    activeKey={`notas`}
                  >
                    <MetadataNotes setDisabled={setDisabled} />
                  </Tabs.TabPane>
                  {((answers && answers.length > 0) ||
                    hasChatbotTags ||
                    hasDocumentSubmissions) && (
                    <Tabs.TabPane
                      tab={
                        <div>
                          <Icon type="question-circle" />
                          {t("opportunity:process.candidateDetails.screening")}
                        </div>
                      }
                      activeKey={`preguntas`}
                      key={`preguntas`}
                    >
                      {((answers && answers.length > 0) || hasChatbotTags) && (
                        <Answers
                          answers={answers}
                          chatbotTags={chatbotTags}
                          extraPanels={
                            hasDocumentSubmissions
                              ? [
                                  {
                                    key: "documents",
                                    title: t(
                                      "opportunity:process.candidateDetails.documents.title"
                                    ),
                                    icon: "file-done",
                                    content: (
                                      <CandidateDocumentSubmissions
                                        submissions={documentSubmissions}
                                        t={t}
                                      />
                                    )
                                  }
                                ]
                              : []
                          }
                          t={t}
                        />
                      )}
                      {hasDocumentSubmissions &&
                        !(
                          (answers && answers.length > 0) ||
                          hasChatbotTags
                        ) && (
                          <Answers
                            answers={[]}
                            extraPanels={[
                              {
                                key: "documents",
                                title: t(
                                  "opportunity:process.candidateDetails.documents.title"
                                ),
                                icon: "file-done",
                                content: (
                                  <CandidateDocumentSubmissions
                                    submissions={documentSubmissions}
                                    t={t}
                                  />
                                )
                              }
                            ]}
                            t={t}
                          />
                        )}
                    </Tabs.TabPane>
                  )}
                  {chatTab}
                  {sentiment && sentiment.totalFrames !== 0 && (
                    <Tabs.TabPane
                      tab={
                        <div>
                          <Icon type="smile" />
                          {t(
                            "opportunity:process.candidateDetails.sentimentalAnalysis"
                          )}
                        </div>
                      }
                      activeKey={`sentimiento`}
                      key={`sentimiento`}
                    >
                      <SentimentalAnalysis sentiment={sentiment} />
                    </Tabs.TabPane>
                  )}
                  {hasTests && (
                    <Tabs.TabPane
                      tab={
                        <div data-test="candidate-modal-tests-tab">
                          <Icon type="flag" />
                          {t(
                            "opportunity:process.candidateDetails.tests.title"
                          )}
                        </div>
                      }
                      activeKey={`tests`}
                      key={`tests`}
                    >
                      <Tests />
                    </Tabs.TabPane>
                  )}
                  {candidate.history && (
                    <Tabs.TabPane
                      tab={
                        <div data-test="candidate-modal-history-tab">
                          <Icon type="flag" />
                          {t(
                            "opportunity:process.candidateDetails.historyTitle"
                          )}
                        </div>
                      }
                      activeKey={`history`}
                      key={`history`}
                    >
                      <CandidateHistory
                        isActive={currentTab === "history"}
                        steps={steps}
                        stepsOrder={stepsOrder}
                        candidate={candidate}
                      />
                    </Tabs.TabPane>
                  )}
                </Tabs>
              </CandidateTabsPanel>
            </ModalContainer>
          </div>
        )}
      </StyledModal>
    </>
  );
};

const enhacer = compose(
  withRouter,
  withTranslation(["opportunity", "form", "global"]),
  connect(state => ({
    suggestedList: R.pathOr(
      [],
      ["candidates", "lists", "suggested", "candidates"],
      state
    ),
    opportunityId: R.path(["opportunity", "selected", "id"], state),
    companyId: R.path(["company", "id"], state),
    sentiment: R.pathOr(
      "",
      ["candidates", "selected", "sentimentAnalysis"],
      state
    ),
    hasTests: R.either(
      R.pathEq(
        ["candidates", "selected", "englishTest", "status"],
        "Success",
        state
      ),
      R.pipe(
        R.always(
          R.concat(
            R.pathOr([], ["candidates", "selected", "humanTests"], state),
            R.pathOr([], ["candidates", "selected", "tests"], state)
          )
        ),
        R.filter(
          ({ serviceCallResult, serviceCallRequest }) =>
            serviceCallResult === "success" &&
            serviceCallRequest &&
            isValidJSON(serviceCallRequest)
        ),
        R.length,
        R.lt(0)
      )()
    ),
    hasRank: R.path(["company", "services", "rank"], state),
    unreadMessages: R.pipe(
      R.pathOr([], ["messages", "newMessages"]),
      R.filter(
        R.both(
          R.propEq(
            "idCandidate",
            R.path(["candidates", "selected", "id"], state)
          ),
          R.propEq(
            "idOpportunity",
            R.path(["opportunity", "selected", "id"], state)
          )
        )
      ),
      R.length
    )(state)
  })),
  withProps(({ candidate, opportunityId, companyId }) => ({
    candidate: R.evolve(
      {
        answers: R.filter(
          R.whereEq({
            opportunity: opportunityId,
            company: companyId
          })
        )
      },
      candidate
    )
  }))
);

export default enhacer(CandidateModal);
